import dayjs from 'dayjs';
import {
  EligibilityFormItemFragment,
  QuestionnaireResponseAnswerFragment,
  TimelineEligibilityFormResource,
} from '@/pages/PatientTimelinePage/graphql';
import {
  ELIGIBILITY_ANSWER_LABELS,
  ELIGIBILITY_COLUMNS,
  ELIGIBILITY_GROUP_ITEMS,
  ELIGIBILITY_GROUP_LABELS,
  getEligibilityItemLabel,
  getEligibilityStepLabel,
} from './eligibilityLabels';

/** Canonical url of the v21 eligibility form. The QR query filters on it. */
export const ELIGIBILITY_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/eligibility-v21';

/** Marks a value we inferred rather than one the patient typed. */
export const ELIGIBILITY_DERIVED_VALUE_EXTENSION = 'http://bariendo.com/fhir/extension/questionnaire-derived-value';

/**
 * How long an `in-progress` QR may sit untouched before the card calls it a
 * drop-off. Deliberately a render-time threshold and not a stored value: a lead
 * who resumes an hour later flips back to "in progress" for free, where a
 * written-once `dropped-off` would be permanently wrong.
 */
export const ELIGIBILITY_DROP_OFF_THRESHOLD_MINUTES = 30;

export const EligibilityFinalActionKind = {
  SCHEDULED: 'scheduled',
  CALLED_LIVE: 'called-live',
  NOT_A_FIT: 'not-a-fit',
  IN_PROGRESS: 'in-progress',
  DROPPED_OFF: 'dropped-off',
  COMPLETED: 'completed',
} as const;

export type EligibilityFinalActionKind = (typeof EligibilityFinalActionKind)[keyof typeof EligibilityFinalActionKind];

export interface EligibilityFinalAction {
  kind: EligibilityFinalActionKind;
  label: string;
  /** Slot, ineligible reason, or reached step, depending on `kind`. */
  detail?: string;
}

export interface EligibilityCollapsedField {
  label: string;
  value: string;
  /** Present when the value carries a `questionnaire-derived-value` extension. */
  derivedFrom?: string;
}

const ABSENT = '—';

/** Depth-first walk over group -> leaf items. */
function* iterateItems(items: EligibilityFormItemFragment[] | undefined): Generator<EligibilityFormItemFragment> {
  for (const item of items ?? []) {
    yield item;
    yield* iterateItems(item.item);
  }
}

function findEligibilityItem(
  qr: TimelineEligibilityFormResource | undefined,
  linkId: string
): EligibilityFormItemFragment | undefined {
  for (const item of iterateItems(qr?.item)) {
    if (item.linkId === linkId) {
      return item;
    }
  }
  return undefined;
}

/** First answer for `linkId`, searched across every group. */
export function findEligibilityAnswer(
  qr: TimelineEligibilityFormResource | undefined,
  linkId: string
): QuestionnaireResponseAnswerFragment | undefined {
  return findEligibilityItem(qr, linkId)?.answer?.[0];
}

/**
 * The tooltip source text for a derived item, or undefined when the value came
 * from the patient. Derived-ness is data on the resource, never a hardcoded
 * list here, so the copy travels with the answer.
 */
export function getEligibilityDerivedSource(
  qr: TimelineEligibilityFormResource | undefined,
  linkId: string
): string | undefined {
  const extension = findEligibilityItem(qr, linkId)?.extension;
  return extension?.find((e) => e.url === ELIGIBILITY_DERIVED_VALUE_EXTENSION)?.valueString;
}

/** Renders whichever `value[x]` an answer carries as display text. */
export function formatEligibilityAnswer(answer: QuestionnaireResponseAnswerFragment | undefined): string | undefined {
  if (!answer) {
    return undefined;
  }
  // Internal short-forms (ESG / IGB / SIS / TORe) are the codes themselves, so
  // prefer `code` over `display` — patient-facing names never appear in Orbit.
  if (answer.valueCoding) {
    return answer.valueCoding.code ?? answer.valueCoding.display ?? undefined;
  }
  if (answer.valueString) {
    return answer.valueString;
  }
  if (answer.valueDateTime) {
    return formatEligibilitySlot(answer.valueDateTime);
  }
  if (answer.valueDate) {
    return dayjs(answer.valueDate).format('MMM D, YYYY');
  }
  // `typeof`, not `!== undefined`: Medplum's GraphQL returns every unselected
  // `value[x]` in the fragment as `null`, and `null !== undefined` is true — so
  // an `!== undefined` guard on a numeric field matched on every answer that had
  // no number and rendered the literal string "null". A boolean answer arrives as
  // `{ valueBoolean: true, valueDecimal: null }`, and the decimal branch below
  // used to win. Hand-built fixtures omit the key entirely, so unit tests never
  // saw it (BAR-2596).
  if (typeof answer.valueQuantity?.value === 'number') {
    const { value, unit } = answer.valueQuantity;
    return unit ? `${value} ${unit}` : `${value}`;
  }
  if (typeof answer.valueDecimal === 'number') {
    return `${answer.valueDecimal}`;
  }
  if (typeof answer.valueBoolean === 'boolean') {
    return answer.valueBoolean ? 'Yes' : 'No';
  }
  return undefined;
}

/** Canon: times as `11:30am`. */
function formatEligibilitySlot(value: string): string {
  return dayjs(value).format('MMM D, YYYY h:mma').replace('AM', 'am').replace('PM', 'pm');
}

/**
 * Units bariendo-be may write on the height quantity. It collapses the lead's
 * feet + inches entry into total inches (`{ value: 66, unit: 'in', code:
 * '[in_i]' }`), so inches are the only form this converts; anything else is
 * left to the generic quantity formatter rather than mis-converted.
 */
const HEIGHT_INCH_UNITS = new Set(['in', 'inch', 'inches', '[in_i]']);

/**
 * Canon: heights as `5'6"`, matching `cmToFeetInches` in `services/patient.ts`
 * so the card and the BMI section agree. Returns undefined when the answer is
 * not a positive inch quantity, which the caller reads as "format it the
 * ordinary way".
 */
export function formatEligibilityHeight(answer: QuestionnaireResponseAnswerFragment | undefined): string | undefined {
  const quantity = answer?.valueQuantity;
  if (quantity?.value === undefined || !(quantity.value > 0)) {
    return undefined;
  }
  if (!HEIGHT_INCH_UNITS.has((quantity.unit ?? quantity.code ?? '').toLowerCase())) {
    return undefined;
  }
  // Round before splitting, so 71.6 reads 6'0" and never 5'12".
  const totalInches = Math.round(quantity.value);
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
}

/** Canon: durations as `6m 04s`. */
export function formatEligibilityDuration(answer: QuestionnaireResponseAnswerFragment | undefined): string | undefined {
  const quantity = answer?.valueQuantity;
  if (quantity?.value === undefined) {
    return undefined;
  }
  const unit = (quantity.unit ?? quantity.code ?? 's').toLowerCase();
  const totalSeconds = unit.startsWith('min') || unit === 'm' ? quantity.value * 60 : quantity.value;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * Display text for one item, applying the per-item answer-label map. Repeating
 * items (`conditions`) join every answer.
 */
export function formatEligibilityItemValue(
  item: EligibilityFormItemFragment | undefined,
  linkId: string
): string | undefined {
  if (!item?.answer?.length) {
    return undefined;
  }

  if (linkId === 'time-to-complete') {
    return formatEligibilityDuration(item.answer[0]);
  }

  if (linkId === 'height') {
    const feetInches = formatEligibilityHeight(item.answer[0]);
    if (feetInches !== undefined) {
      return feetInches;
    }
    // Not inches — fall through and render the quantity as stored.
  }

  const labels = ELIGIBILITY_ANSWER_LABELS[linkId];
  const parts = item.answer
    .map((answer) => {
      const raw = formatEligibilityAnswer(answer);
      if (raw === undefined) {
        return undefined;
      }
      if (linkId === 'reached-step') {
        return getEligibilityStepLabel(raw);
      }
      return labels?.[raw] ?? raw;
    })
    .filter((part): part is string => !!part);

  return parts.length > 0 ? parts.join(', ') : undefined;
}

function answerText(qr: TimelineEligibilityFormResource | undefined, linkId: string): string | undefined {
  return formatEligibilityItemValue(findEligibilityItem(qr, linkId), linkId);
}

export interface EligibilityGroup {
  linkId: string;
  label: string;
  fields: EligibilityCollapsedField[];
}

/**
 * The expanded view: two columns of groups, each group carrying only the items
 * the QR actually answered. Order comes from the label map, not from however the
 * QR happens to be assembled.
 */
export function getEligibilityGroups(
  qr: TimelineEligibilityFormResource | undefined,
  column: 'left' | 'right'
): EligibilityGroup[] {
  return ELIGIBILITY_COLUMNS[column]
    .map((groupLinkId) => {
      const fields = (ELIGIBILITY_GROUP_ITEMS[groupLinkId] ?? []).flatMap((linkId) => {
        const item = findEligibilityItem(qr, linkId);
        const value = formatEligibilityItemValue(item, linkId);
        if (value === undefined) {
          return [];
        }
        return [
          {
            label: getEligibilityItemLabel(linkId, item?.text),
            value,
            derivedFrom: getEligibilityDerivedSource(qr, linkId),
          },
        ];
      });

      return { linkId: groupLinkId, label: ELIGIBILITY_GROUP_LABELS[groupLinkId] ?? groupLinkId, fields };
    })
    .filter((group) => group.fields.length > 0);
}

/**
 * What the patient did last — the card leads with this because it is what
 * decides whether anyone has to act. Only the three terminal outcomes are
 * stored; in-progress vs. dropped-off is derived from `status` + `lastUpdated`.
 */
export function deriveEligibilityFinalAction(
  qr: TimelineEligibilityFormResource | undefined,
  now: Date = new Date()
): EligibilityFinalAction {
  const storedAction = findEligibilityAnswer(qr, 'final-action')?.valueCoding?.code;

  if (storedAction === EligibilityFinalActionKind.SCHEDULED) {
    return {
      kind: EligibilityFinalActionKind.SCHEDULED,
      label: 'Scheduled intro call',
      detail: answerText(qr, 'call-slot'),
    };
  }

  if (storedAction === EligibilityFinalActionKind.CALLED_LIVE) {
    // Call duration lives on the Appointment / Communication side, not the QR.
    return { kind: EligibilityFinalActionKind.CALLED_LIVE, label: 'Called our team live' };
  }

  if (storedAction === EligibilityFinalActionKind.NOT_A_FIT) {
    return {
      kind: EligibilityFinalActionKind.NOT_A_FIT,
      label: 'Not a fit',
      detail: answerText(qr, 'ineligible-reason'),
    };
  }

  const reachedStep = answerText(qr, 'reached-step');

  if (qr?.status === 'completed') {
    return { kind: EligibilityFinalActionKind.COMPLETED, label: 'Completed', detail: reachedStep };
  }

  const lastUpdated = qr?.meta?.lastUpdated;
  const idleMinutes = lastUpdated ? dayjs(now).diff(dayjs(lastUpdated), 'minute') : 0;

  // Missing lastUpdated stays "in progress" rather than claiming a drop-off we
  // cannot evidence.
  if (lastUpdated && idleMinutes >= ELIGIBILITY_DROP_OFF_THRESHOLD_MINUTES) {
    return { kind: EligibilityFinalActionKind.DROPPED_OFF, label: 'Dropped off', detail: reachedStep };
  }

  return { kind: EligibilityFinalActionKind.IN_PROGRESS, label: 'In progress', detail: reachedStep };
}

/**
 * A lead from the employer coverage form (app.everself.com/signup/carrum), which
 * bariendo-be marks with `source-form` (BAR-2835). `track` can't tell: a D2C lead
 * who checks an employer benefit is `employer` too.
 */
export function isEmployerCoverageForm(qr: TimelineEligibilityFormResource | undefined): boolean {
  return answerText(qr, 'source-form') === 'employer-coverage';
}

/**
 * A lead who picked "Insurance or employer benefit" on the payment page
 * (BAR-2902). Read off the raw code, since `answerText` returns the label.
 */
export function isCoverageLead(qr: TimelineEligibilityFormResource | undefined): boolean {
  return findEligibilityAnswer(qr, 'payment-mode')?.valueCoding?.code === 'coverage';
}

function collapsedRowLinkIds(qr: TimelineEligibilityFormResource | undefined): string[] {
  if (isEmployerCoverageForm(qr)) {
    return ['employer', 'employer-covered'];
  }
  if (isCoverageLead(qr)) {
    return ['insurer', 'coverage-result'];
  }
  return ['nearest-location', 'interested-in'];
}

/**
 * Exactly three fields, one line each — so a 40-card timeline still scans at
 * about a second per card. Everything else sits behind *Show more*. The employer
 * coverage form never asks ZIP or procedure, so its card shows the employer rows
 * instead of two that would always read "—". A coverage lead shows what the care
 * team has to verify: the insurer and the coverage result.
 */
export function getEligibilityCollapsedFields(
  qr: TimelineEligibilityFormResource | undefined,
  now: Date = new Date()
): EligibilityCollapsedField[] {
  const finalAction = deriveEligibilityFinalAction(qr, now);
  const rows = collapsedRowLinkIds(qr);

  return [
    {
      label: 'Final action',
      value: finalAction.detail ? `${finalAction.label} · ${finalAction.detail}` : finalAction.label,
      derivedFrom: getEligibilityDerivedSource(qr, 'final-action'),
    },
    ...rows.map((linkId) => ({
      label: getEligibilityItemLabel(linkId),
      value: answerText(qr, linkId) ?? ABSENT,
      derivedFrom: getEligibilityDerivedSource(qr, linkId),
    })),
  ];
}
